import React, { useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import AddUserManagementFormLayout from "../../layouts/AddUserManagementFormLayout/AddUserManagementFormLayout";
import CustomInput from "../../components/CustomInput/CustomInput";
import {
  getLocalizedMessage,
  getValueOnCondition,
} from "../../UtilityFunctions/utils";
import { Text } from "react_reusable_components";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import { BasicDetailsContainerStyles } from "../BasicDetailsContainer/BasicDetailsContainer";
import ProductCategoryService from "../../services/product-category-service";
import UserManagementService from "../../services/user-management-service";
import Style from "./UserRoleDetailsContainer.module.scss";
import { addUserFormFieldNames } from "../../validationSchema/addUserManagementSchema";
import { userRole as rolesOfUser, userRole } from "../../constants";
import GlobalIdFieldComponent from "../../components/GlobalIdFieldComponent/GlobalIdFieldComponent";
import { UserContext } from "../../globalContext/Users/userProvider";
import { AuthContext } from "../../globalContext/auth/authProvider";

const UserRoleDetailsContainer = ({
  handleChange,
  values,
  errors,
  setFieldValue,
  setCountryId,
  touched,
  setIsRepairAuditForDNSD,
  setIsRepairAuditForTM,
  setIsOperationAuditForTM,
  handleBlur,
  validateField,
  setFieldTouched,
  isEditMode,
}) => {//NOSONAR
  const intl = useIntl();

  const [authState] = useContext(AuthContext);

  const [countries, setCountries] = useState([]);
  const [userRoles, setAllUserRoles] = useState([]);
  const rolesId = values[addUserFormFieldNames.ROLES_ID];
  const [userState] = useContext(UserContext);

  useEffect(() => {
    ProductCategoryService.getCountries(intl).then((data) => {
      setCountries(data?.result);
    });
    UserManagementService.getRoles().then((data) => {
      setAllUserRoles(data?.result);
    });
  }, [intl]);

  useEffect(() => {
    if (!rolesId?.includes(rolesOfUser.TM)) {
      setIsOperationAuditForTM(false);
      setIsRepairAuditForTM(false);
    }
    if (!rolesId?.includes(rolesOfUser.DNSD)) {
      setIsRepairAuditForDNSD(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesId]);

  useEffect(() => {
    if (authState?.currentRole?.id === userRole.CA) {
      setFieldValue([addUserFormFieldNames.COUNTRY_ID], countries?.[0]?.id);
      setCountryId(countries?.[0]?.id);
    }
  }, [setFieldValue, countries, authState.currentRole]);

  const roleChangeHandler = (e) => {
    setFieldValue([addUserFormFieldNames.ROLES_ID], e, {
      shouldValidate: true,
    });
  };

  const countryChangeHandler = (e) => {
    setFieldValue([addUserFormFieldNames.COUNTRY_ID], e, {
      shouldValidate: true,
    });
    setCountryId(e);
  };

  const showCountryField = getShowCountryField(values);

  const showEmployeeField = getShowEmployeeId(values);

  const showGlobalIdOnlyWhenDNSDSelected =
    getShowGlobalIdWhenDNSDSelected(values);

  const validateCountry = () => {
    setFieldTouched(addUserFormFieldNames.COUNTRY_ID, true);
    validateField(addUserFormFieldNames.COUNTRY_ID); // Manually trigger validation for field1
  };

  const validateRole = () => {
    setFieldTouched(addUserFormFieldNames.ROLES_ID, true);
    validateField(addUserFormFieldNames.ROLES_ID); // Manually trigger validation for field1
  };

  return (
    <AddUserManagementFormLayout
      headerTopSection={
        <Text style={BasicDetailsContainerStyles.basicDetailsText}>
          {getLocalizedMessage(intl, "addUser.text.userRoleDetails")}
        </Text>
      }
      leftSection={
        <CustomSelect
          label={getLocalizedMessage(intl, "addUser.label.selectUserRole")}
          required={true}
          showSearch={false}
          placeholder={getLocalizedMessage(
            intl,
            "addUser.selectUserRole.placeholder"
          )}
          type="multipleSelect"
          mode="multiple"
          options={userRoles?.map((role) => {
            return {
              label: (
                <Text className={`${Style["select-item-style"]}`}>
                  {role?.fullName}
                </Text>
              ),
              value: role?.id,
              disabled:
                (role?.id !== userRole.DNSD &&
                  values[addUserFormFieldNames.ROLES_ID]?.includes(
                    userRole.DNSD
                  )) ||
                (values[addUserFormFieldNames.ROLES_ID]?.length > 0 &&
                  !values[addUserFormFieldNames.ROLES_ID]?.includes(
                    userRole.DNSD
                  ) &&
                  role?.id === userRole.DNSD),
            };
          })}
          name={addUserFormFieldNames.ROLES_ID}
          value={values[addUserFormFieldNames.ROLES_ID]}
          onChange={(e) => roleChangeHandler(e)}
          onFocus={validateRole}
          error={
            touched[addUserFormFieldNames.ROLES_ID] &&
            errors[addUserFormFieldNames.ROLES_ID]
          }
        />
      }
      middleSection={
        <>
          {showGlobalIdOnlyWhenDNSDSelected && (
            <GlobalIdFieldComponent
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              onBlur={handleBlur}
            />
          )}

          {showEmployeeField && (
            <CustomInput
              required
              label={getLocalizedMessage(intl, "addUser.addUserEmployeeId")}
              placeholder={getLocalizedMessage(
                intl,
                "addUser.addUserEmployeeId.placeholder"
              )}
              size={"small"}
              name={addUserFormFieldNames.EMP_ID}
              value={values[addUserFormFieldNames.EMP_ID]}
              onChange={handleChange}
              error={getValueOnCondition({
                condition: touched[addUserFormFieldNames.EMP_ID],
                trueValue: errors[addUserFormFieldNames.EMP_ID],
                falseValue: null,
              })}
              onBlur={handleBlur}
            />
          )}
        </>
      }
      rightSection={
        showCountryField && (
          <CustomSelect
            label={getLocalizedMessage(intl, "addUser.label.country")}
            required={true}
            placeholder={getLocalizedMessage(
              intl,
              "addUser.selectUserCountry.placeholder"
            )}
            showSearch={false}
            options={countries?.map((country) => {
              return {
                label: (
                  <Text className={`${Style["select-item-style"]}`}>
                    {country?.name}
                  </Text>
                ),
                value: country?.id,
              };
            })}
            name={addUserFormFieldNames.COUNTRY_ID}
            value={values[addUserFormFieldNames.COUNTRY_ID] || null}
            onChange={(e) => countryChangeHandler(e)}
            onFocus={validateCountry}
            error={getValueOnCondition({
              condition: touched[addUserFormFieldNames.COUNTRY_ID],
              trueValue: errors[addUserFormFieldNames.COUNTRY_ID],
              falseValue: null,
            })}
            disabled={
              (isEditMode && userState?.userData?.country?.id) ||
              authState?.currentRole?.id === userRole?.CA
            }
          />
        )
      }
    />
  );
};

export default UserRoleDetailsContainer;

function getShowCountryField(values) {
  const rolesIdArray = values[addUserFormFieldNames.ROLES_ID] || [];
  const isRolesIdNotEmpty = rolesIdArray.length > 0;

  if (isRolesIdNotEmpty) {
    const isSingleRole = rolesIdArray.length === 1;
    const isAdminRole =
      isSingleRole && rolesIdArray.includes(rolesOfUser.ADMIN);

    return isAdminRole ? false : true;
  } else {
    return false;
  }
}

function getShowEmployeeId(values) {
  const rolesIdArray = values[addUserFormFieldNames.ROLES_ID] || [];
  const isRolesIdNotEmpty = rolesIdArray.length > 0;

  if (isRolesIdNotEmpty) {
    const isSingleRole = rolesIdArray.length === 1;
    const isDNSDSelected =
      isSingleRole && rolesIdArray.includes(rolesOfUser.DNSD);
    return isDNSDSelected ? false : true;
  } else {
    return false;
  }
}

function getShowGlobalIdWhenDNSDSelected(values) {
  const rolesIdArray = values[addUserFormFieldNames.ROLES_ID] || [];
  const isRolesIdNotEmpty = rolesIdArray.length > 0;
  const isDNSDSelected =
    isRolesIdNotEmpty &&
    rolesIdArray.length === 1 &&
    rolesIdArray.includes(rolesOfUser.DNSD);
  return isDNSDSelected ? true : false;
}
